import type { ReactNode } from 'react';
import { usePermissionState } from './PermissionProvider.js';
import type { PermissionState } from './resolve.js';

export interface PermissionGateProps {
  /** PERM-* catalogue code — never ROLE-* names. */
  perm?: string;
  children: ReactNode;
  fallback?: ReactNode;
  readonlyRender?: ReactNode;
  disabledReason?: string;
}

export function renderPermissionState(
  state: PermissionState,
  children: ReactNode,
  fallback: ReactNode | undefined,
  readonlyRender: ReactNode | undefined,
  disabledReason?: string,
): ReactNode {
  switch (state) {
    case 'hidden':
      return fallback ?? null;
    case 'disabled':
      return (
        <fieldset disabled title={disabledReason} aria-disabled="true">
          {children}
        </fieldset>
      );
    case 'readonly':
      return (
        readonlyRender ?? (
          <div aria-readonly="true" data-permission-state="readonly">
            {children}
          </div>
        )
      );
    case 'editable':
      return children;
    default:
      return children;
  }
}

export function PermissionGate({
  perm,
  children,
  fallback,
  readonlyRender,
  disabledReason,
}: PermissionGateProps) {
  const state = usePermissionState(perm);
  return <>{renderPermissionState(state, children, fallback, readonlyRender, disabledReason)}</>;
}
